import React, { useState, useEffect } from 'react';
import { configService } from '../../services/api';
import PromptEditorModal from '../PromptEditorModal';
import CompanyDocumentsManager from '../CompanyDocumentsManager';
import UserManagementTab from '../UserManagementTab';
import LlmModelsTab from '../LlmModelsTab';
import FeedbackAdminTab from '../FeedbackAdminTab';

const ConfigurationTab = () => {
  const [activeSection, setActiveSection] = useState('prompts');
  const [generalConfig, setGeneralConfig] = useState({
    llmModel: 'gemini-pro',
    timeout: 30,
    maxFileSize: 50,
    maxTokens: 4000,
    temperature: 0.7
  });
  const [prompts, setPrompts] = useState([]);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [promptContent, setPromptContent] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    loadConfiguration();
    loadPrompts();
  }, []);

  const loadConfiguration = async () => {
    try {
      setLoading(true);
      const response = await configService.getConfiguration();
      const config = response.data || {};
      setGeneralConfig(prev => ({ ...prev, ...config }));
    } catch (error) {
      console.error('Error loading configuration:', error);
      setMessage({ type: 'error', text: 'Failed to load configuration' });
    } finally {
      setLoading(false);
    }
  };

  const loadPrompts = async () => {
    try {
      const response = await configService.getPrompts();
      const promptsList = response.data || [];
      setPrompts(Array.isArray(promptsList) ? promptsList : []);
      
      // Don't auto-select or load any prompt - let user click on a card
    } catch (error) {
      console.error('Error loading prompts:', error);
      setPrompts([]);
      setMessage({ type: 'error', text: 'Failed to load prompts' });
    }
  };

  const loadPromptContent = async (promptId) => {
    try {
      setLoading(true);
      const response = await configService.getPromptContent(promptId);
      const promptData = response.data || {};
      setPromptContent(promptData.content || '');
      setSelectedPrompt(promptData);
      setIsModalOpen(true); // Open modal when prompt is loaded
    } catch (error) {
      console.error('Error loading prompt content:', error);
      setMessage({ type: 'error', text: 'Failed to load prompt content' });
    } finally {
      setLoading(false);
    }
  };

  const saveConfiguration = async () => {
    try {
      setSaving(true);
      await configService.saveConfiguration(generalConfig);
      setMessage({ type: 'success', text: 'Configuration saved successfully' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save configuration' });
    } finally {
      setSaving(false);
    }
  };

  const savePrompt = async (newContent) => {
    if (!selectedPrompt) return;

    try {
      setSaving(true);
      const promptId = selectedPrompt.id || selectedPrompt.name;
      await configService.savePrompt(promptId, newContent);
      setMessage({ type: 'success', text: 'Prompt saved successfully' });
      
      // Update local content
      setPromptContent(newContent);
      
      // Close modal
      setIsModalOpen(false);
      
      // Reload prompts list to get updated version
      await loadPrompts();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save prompt' });
    } finally {
      setSaving(false);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const handleConfigChange = (field, value) => {
    setGeneralConfig(prev => ({ ...prev, [field]: value }));
  };

  const clearMessage = () => {
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  useEffect(() => {
    if (message.text) clearMessage();
  }, [message]);

  return (
    <div className="card">
      {/* Message Display */}
      {message.text && (
        <div className={`mb-4 p-3 rounded-lg ${
          message.type === 'success' 
            ? 'bg-green-100 text-green-800 border border-green-200' 
            : 'bg-red-100 text-red-800 border border-red-200'
        }`}>
          {message.text}
        </div>
      )}

      {/* Tab Navigation */}
      <div className="mb-6" style={{borderBottom: '1px solid var(--tqa-border-dark)'}}>
        <nav className="flex space-x-8">
          {/* General tab hidden for now - no general settings yet */}
          {/* <button
            onClick={() => setActiveSection('general')}
            className={`tab-btn ${activeSection === 'general' ? 'active' : ''}`}
          >
            General
          </button> */}
          <button
            onClick={() => setActiveSection('prompts')}
            className={`tab-btn ${activeSection === 'prompts' ? 'active' : ''}`}
          >
            Prompts
          </button>
          <button
            onClick={() => setActiveSection('models')}
            className={`tab-btn ${activeSection === 'models' ? 'active' : ''}`}
          >
            LLM Models
          </button>
          <button
            onClick={() => setActiveSection('documents')}
            className={`tab-btn ${activeSection === 'documents' ? 'active' : ''}`}
          >
            Company Documents
          </button>
          <button
            onClick={() => setActiveSection('users')}
            className={`tab-btn ${activeSection === 'users' ? 'active' : ''}`}
          >
            Users
          </button>
          <button
            onClick={() => setActiveSection('feedback')}
            className={`tab-btn ${activeSection === 'feedback' ? 'active' : ''}`}
          >
            Feedback
          </button>
        </nav>
      </div>

      {/* General Settings */}
      {activeSection === 'general' && (
        <div className="space-y-6">
          <h3 className="text-lg font-semibold mb-4" style={{color: 'rgba(255,255,255,0.85)'}}>General Settings</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* LLM Model Selection */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{color: 'rgba(255,255,255,0.65)'}}>
                LLM Model
              </label>
              <select
                value={generalConfig.llmModel}
                onChange={(e) => handleConfigChange('llmModel', e.target.value)}
                className="w-full px-3 py-2 rounded-md shadow-sm focus:outline-none"
                style={{background: 'var(--tqa-surface-2)', border: '1px solid var(--tqa-border-dark)', color: 'rgba(255,255,255,0.85)'}}
              >
                <option value="gemini-pro">Gemini Pro</option>
                <option value="gemini-1.5-pro">Gemini 1.5 Pro</option>
                <option value="gpt-4">GPT-4</option>
                <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
                <option value="claude-3">Claude 3</option>
              </select>
            </div>

            {/* Timeout */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{color: 'rgba(255,255,255,0.65)'}}>
                Request Timeout (seconds)
              </label>
              <input
                type="number"
                min="10"
                max="300"
                value={generalConfig.timeout}
                onChange={(e) => handleConfigChange('timeout', parseInt(e.target.value))}
                className="w-full px-3 py-2 rounded-md shadow-sm focus:outline-none"
                style={{background: 'var(--tqa-surface-2)', border: '1px solid var(--tqa-border-dark)', color: 'rgba(255,255,255,0.85)'}}
              />
            </div>

            {/* Max File Size */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{color: 'rgba(255,255,255,0.65)'}}>
                Max File Size (MB)
              </label>
              <input
                type="number"
                min="1"
                max="500"
                value={generalConfig.maxFileSize}
                onChange={(e) => handleConfigChange('maxFileSize', parseInt(e.target.value))}
                className="w-full px-3 py-2 rounded-md shadow-sm focus:outline-none"
                style={{background: 'var(--tqa-surface-2)', border: '1px solid var(--tqa-border-dark)', color: 'rgba(255,255,255,0.85)'}}
              />
            </div>

            {/* Max Tokens */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{color: 'rgba(255,255,255,0.65)'}}>
                Max Tokens
              </label>
              <input
                type="number"
                min="1000"
                max="32000"
                value={generalConfig.maxTokens}
                onChange={(e) => handleConfigChange('maxTokens', parseInt(e.target.value))}
                className="w-full px-3 py-2 rounded-md shadow-sm focus:outline-none"
                style={{background: 'var(--tqa-surface-2)', border: '1px solid var(--tqa-border-dark)', color: 'rgba(255,255,255,0.85)'}}
              />
            </div>

            {/* Temperature */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{color: 'rgba(255,255,255,0.65)'}}>
                Temperature (0.0 - 1.0)
              </label>
              <input
                type="number"
                min="0"
                max="1"
                step="0.1"
                value={generalConfig.temperature}
                onChange={(e) => handleConfigChange('temperature', parseFloat(e.target.value))}
                className="w-full px-3 py-2 rounded-md shadow-sm focus:outline-none"
                style={{background: 'var(--tqa-surface-2)', border: '1px solid var(--tqa-border-dark)', color: 'rgba(255,255,255,0.85)'}}
              />
            </div>
          </div>

          <div className="flex justify-end">
            <button
              onClick={saveConfiguration}
              disabled={saving}
              className="btn-primary"
            >
              {saving ? 'Saving...' : 'Save Configuration'}
            </button>
          </div>
        </div>
      )}

      {/* Prompts Management */}
      {activeSection === 'prompts' && (
        <div className="flex flex-col min-h-0">
          <h3 className="text-lg font-semibold mb-4" style={{color: 'rgba(255,255,255,0.85)'}}>LLM Prompts</h3>
          <p className="text-sm mb-6" style={{color: 'rgba(255,255,255,0.5)'}}>
            Manage system prompts used by AI models for code analysis and chat assistance.
          </p>

          <div className="max-h-[62vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Array.isArray(prompts) && prompts.length > 0 ? (
              prompts.map((prompt) => (
                <div
                  key={prompt.id}
                  className="rounded-lg p-5 transition-shadow"
                  style={{background: 'var(--tqa-surface)', border: '1px solid var(--tqa-border-dark)'}}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h4 className="text-lg font-semibold" style={{color: 'rgba(255,255,255,0.85)'}}>
                        {prompt.displayName}
                      </h4>
                      <p className="text-sm mt-1" style={{color: 'rgba(255,255,255,0.5)'}}>
                        {prompt.description || 'No description available'}
                      </p>
                    </div>
                    <span
                      className="ml-3 px-2 py-1 text-xs font-medium rounded-full"
                      style={prompt.isActive
                        ? {background: 'rgba(130,255,207,0.15)', color: 'var(--tqa-green)'}
                        : {background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.5)'}}
                    >
                      {prompt.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>

                  <div className="flex items-center gap-3 text-xs mb-4" style={{color: 'rgba(255,255,255,0.4)'}}>
                    <span className="flex items-center gap-1">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                      </svg>
                      {prompt.type}
                    </span>
                    <span className="flex items-center gap-1">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      v{prompt.version}
                    </span>
                    <span className="flex items-center gap-1">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {new Date(prompt.updatedAt).toLocaleDateString()}
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => loadPromptContent(prompt.id)}
                      className="btn-primary flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      Edit Prompt
                    </button>
                    <button
                      className="btn-secondary px-3 py-2 rounded-md transition-colors"
                      title="View details"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="col-span-2 text-center py-12" style={{color: 'rgba(255,255,255,0.4)'}}>
                <svg className="w-12 h-12 mx-auto mb-3" style={{color: 'rgba(255,255,255,0.3)'}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p>No prompts available</p>
              </div>
            )}
            </div>
          </div>
        </div>
      )}

      {/* Company Documents Management */}
      {activeSection === 'documents' && (
        <CompanyDocumentsManager />
      )}

      {/* LLM Models Management */}
      {activeSection === 'models' && (
        <LlmModelsTab />
      )}

      {/* User Management */}
      {activeSection === 'users' && (
        <UserManagementTab />
      )}

      {/* Feedback Management */}
      {activeSection === 'feedback' && (
        <FeedbackAdminTab />
      )}

      {/* Prompt Editor Modal */}
      {isModalOpen && selectedPrompt && (
        <PromptEditorModal
          prompt={selectedPrompt}
          content={promptContent}
          onSave={savePrompt}
          onClose={closeModal}
          saving={saving}
        />
      )}

      {loading && (
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{borderColor: 'var(--tqa-green)'}}></div>
        </div>
      )}
    </div>
  );
};

export default ConfigurationTab;